import {
  EditType,
  PositionType,
  type SportPlayedProfileEdit,
  type SportResponseRecord,
  v1PeopleProfileEditsCreate,
  v1PositionsList,
  v1SportsList,
} from '@on3/api';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import {
  Select,
  type SelectOption,
} from '@on3/ui-lib/components/Select/Select';
import { externalApi, useToast } from '@on3/ui-lib/index';
import { yearOptions } from '@on3/ui-lib/utils/date';
import { filterNumber } from '@on3/ui-lib/utils/filters';
import { toNumberOrNull } from '@on3/ui-lib/utils/yup';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { type PlayerSport } from '@public/pages/rivals/[id]/elite/network/profile/details';
import { useEffect, useState } from 'react';
import { number, object } from 'yup';

import styles from './CreateSportModal.module.scss';

interface ICreateSportModalProps {
  sports: SportResponseRecord[];
  playerSports?: PlayerSport[];
  onComplete?: () => void;
}

const schema = object({
  sportKey: number().required().label('Sport'),
  firstYear: number().nullable().label('First year played'),
  lastYear: number().required().label('Last year played'),
  primaryPositionKey: number().required().label('Position'),
  jerseyNumber: number()
    .transform(toNumberOrNull)
    .nullable()
    .label('Jersey number'),
  secondaryPositionKey: number().nullable().label('Secondary position'),
});

export const CreateSportModal = ({
  sports,
  playerSports: ps,
  onComplete = () => {},
}: ICreateSportModalProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { notify } = useToast();

  const [values, setValues] = useState<SportPlayedProfileEdit>({
    $type: 'sportPlayed',
    editType: EditType.Add,
    sportKey: 0,
    firstYear: Number(yearOptions[0].value),
    lastYear: Number(yearOptions[0].value),
  });

  const [sportOptions, setSportOptions] = useState<SelectOption[]>(
    sports.map((s) => ({
      label: s.name || '',
      value: String(s.key),
    })),
  );
  const [positionOptions, setPositionOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!values.sportKey) return;

    try {
      v1PositionsList(externalApi, {
        positionType: PositionType.Player,
        sportKey: values.sportKey,
      }).then((res) =>
        setPositionOptions(
          res.map((p) => ({ label: p.name || '', value: String(p.key) })),
        ),
      );
    } catch (error) {
      notify('Unable to fetch positions', { type: 'error' });
    }
  }, [notify, values.sportKey]);

  useEffect(() => {
    if (sports.length) return;

    try {
      v1SportsList(externalApi).then((res) =>
        setSportOptions(
          res.map((s) => ({
            label: s.name || '',
            value: String(s.key),
          })),
        ),
      );
    } catch (error) {
      notify('Unable to fetch sports', { type: 'error' });
    }
  }, [notify, sports]);

  const handleUpdate = (field: string, value: string | number) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      await v1PeopleProfileEditsCreate(externalApi, player.key, {
        edit: values,
      });

      notify('Sport added successfully.', { type: 'success' });

      onComplete();
    } catch (error) {
      notify('Failed to add sport.', { type: 'error' });
    }
  };

  const isValid =
    !!values.sportKey && !!values.lastYear && !!values.primaryPositionKey;

  return (
    <Form
      className={styles.block}
      onSubmit={handleSubmit}
      schema={schema}
      values={values}
    >
      {({ processing, errors }) => (
        <div className={styles.sport}>
          <h2>Add Sport</h2>
          <div className={styles.meta}>
            <Select
              disabled={!sportOptions.length}
              error={errors.sportKey}
              label="Sport"
              onChange={(e) => handleUpdate(`sportKey`, Number(e.target.value))}
              options={[{ label: 'Select a sport', value: '0' }]
                .concat(sportOptions)
                .filter((s) => !ps?.some((e) => e.key === Number(s.value)))}
              required
              value={String(values.sportKey ?? '')}
            />
            <Input
              error={errors.jerseyNumber}
              label="Jersey Number"
              onChange={(e) =>
                handleUpdate(`jerseyNumber`, filterNumber(e.target.value))
              }
              type="numeric"
              value={String(values.jerseyNumber ?? '')}
            />
          </div>
          <div className={styles.positions}>
            <Select
              disabled={!values.sportKey}
              error={errors.primaryPositionKey}
              label="Position"
              onChange={(e) =>
                handleUpdate(`primaryPositionKey`, Number(e.target.value))
              }
              options={positionOptions.filter(
                (p) => p.value !== String(values.secondaryPositionKey),
              )}
              placeholder="Select a position"
              required
              value={String(values.primaryPositionKey ?? '')}
            />
            <Select
              disabled={!values.sportKey}
              error={errors.secondaryPositionKey}
              label="Secondary Position"
              onChange={(e) =>
                handleUpdate(`secondaryPositionKey`, Number(e.target.value))
              }
              options={positionOptions.filter(
                (p) => p.value !== String(values.primaryPositionKey),
              )}
              placeholder="Select a position"
              value={String(values.secondaryPositionKey ?? '')}
            />
          </div>
          <div className={styles.years}>
            <Select
              error={errors.firstYear}
              label="First Year Played"
              onChange={(e) =>
                handleUpdate(`firstYear`, Number(e.target.value))
              }
              options={yearOptions}
              value={String(values.firstYear ?? '')}
            />
            <Select
              error={errors.lastYear}
              label="Last Year Played"
              onChange={(e) => handleUpdate(`lastYear`, Number(e.target.value))}
              options={yearOptions}
              required
              value={String(values.lastYear ?? '')}
            />
          </div>
          <button disabled={processing || !isValid} type="submit">
            Save
          </button>
        </div>
      )}
    </Form>
  );
};
